import type { AdminResponse } from "../../lib/api";
import { SURVEY_QUESTIONS, type Question } from "../../lib/survey-config";
import { isoZ } from "../../lib/format";

function answerOf(r: AdminResponse, key: string): unknown {
  return (r.answers as unknown as Record<string, unknown>)[key];
}

function lookupOptionLabel(q: Question, value: string): string {
  if (q.type !== "singleSelect" && q.type !== "multiSelect") return value;
  return q.options.find((o) => o.value === value)?.label ?? value;
}

export function ResponseDetail({ response }: { response: AdminResponse }) {
  return (
    <div className="space-y-6">
      <div className="space-y-1 text-xs text-fg-muted">
        <div>
          <span className="font-medium text-fg">Submission-ID:</span>{" "}
          <code className="rounded bg-border/50 px-1">{response.submissionId}</code>
        </div>
        <div>
          <span className="font-medium text-fg">Gestartet:</span> {isoZ(response.startedAt)}
        </div>
        <div>
          <span className="font-medium text-fg">Eingereicht:</span> {isoZ(response.submittedAt)}
        </div>
      </div>

      <div className="space-y-5">
        {SURVEY_QUESTIONS.map((q, i) => (
          <AnswerBlock key={q.id} q={q} idx={i} response={response} />
        ))}
      </div>
    </div>
  );
}

function AnswerBlock({ q, idx, response }: { q: Question; idx: number; response: AdminResponse }) {
  const value = answerOf(response, q.id);
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-mono uppercase tracking-wider text-fg-muted">
        F{idx + 1} · {q.id}
      </h3>
      <p className="font-medium leading-snug">{q.title}</p>
      <div className="text-sm leading-relaxed">
        {renderAnswer(q, value, response)}
      </div>
    </div>
  );
}

function renderAnswer(q: Question, value: unknown, response: AdminResponse) {
  if (q.type === "shortText" || q.type === "longText") {
    if (typeof value !== "string" || !value.trim()) {
      return <span className="italic text-fg-muted">nicht beantwortet</span>;
    }
    return (
      <blockquote className="border-l-2 border-border pl-3 whitespace-pre-wrap text-fg">
        {value}
      </blockquote>
    );
  }
  if (q.type === "singleSelect") {
    if (typeof value !== "string" || !value) {
      return <span className="italic text-fg-muted">nicht beantwortet</span>;
    }
    return (
      <span>
        <code className="rounded bg-border/50 px-1 text-xs">{value}</code>
        {" — "}
        {lookupOptionLabel(q, value)}
      </span>
    );
  }
  if (q.type === "multiSelect") {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    const other = answerOf(response, `${q.id}_other`);
    return (
      <div className="space-y-2">
        {arr.length === 0 ? (
          <span className="italic text-fg-muted">keine Auswahl</span>
        ) : (
          <ul className="space-y-1">
            {arr.map((v) => (
              <li key={v}>
                <code className="rounded bg-border/50 px-1 text-xs">{v}</code>
                {" — "}
                {lookupOptionLabel(q, v)}
              </li>
            ))}
          </ul>
        )}
        {q.allowOther ? (
          <div className="text-xs text-fg-muted">
            <span className="font-medium">Sonstiges:</span>{" "}
            {typeof other === "string" && other.trim() ? other : <em>leer</em>}
          </div>
        ) : null}
      </div>
    );
  }
  if (q.type === "slider") {
    if (typeof value !== "number") return <span className="italic text-fg-muted">—</span>;
    return (
      <span>
        <strong className="text-accent">{value}</strong>
        <span className="text-fg-muted"> / {q.max}</span>
      </span>
    );
  }
  return null;
}
